<template>
  <div :class="$style.dropdownFilterContainer">
    <DropdownOperators
      v-if="hasOperators"
      v-model:operator="internalOperator"
      :filter="filter"
    />
    <div
      v-for="(definition, index) in dropdDefs"
      :key="definition.key"
      class="sb-dropdown-chain-item"
    >
      <template v-if="definition.component === 'sb-dropdown'">
        <sb-dropdown
          v-if="(maxInitialToShow > index || optionsById[index]?.length>0)
            && dsComponentDetails.version === VersionOptions.LEGACY"
          v-bind="definition.props"
          v-model:value="definition.value"
          :data-testid="definition.key"
          :disabled="!(optionsById[index]?.length>0)"
          :options="optionsById[index]"
          @update:value="emitValue($event,index)"
        >
          <template #selection>
            <sb-selected-option>
              {{ getSelected(optionsById[index], definition.value) }}
            </sb-selected-option>
          </template>
          <template v-for="option in optionsById[index]">
            <sb-option
              v-if="option?.id"
              :key="option.id"
              :value="option?.id"
            >
              <p>{{ option?.displayName }}</p>
            </sb-option>
          </template>
        </sb-dropdown>
        <sb-select
          v-if="(maxInitialToShow > index || optionsById[index]?.length>0) && dsComponentDetails.version === 'next'"
          v-bind="definition.props"
          v-model="definition.value"
          :data-testid="definition.key"
          :disabled="!(optionsById[index]?.length>0)"
          :options="optionsById[index]"
          option-label="displayName"
          fluid
          @update:model-value="emitValue($event,index)"
        />
      </template>
      <template v-else-if="definition.component === 'sb-dropdown-search'">
        <sb-dropdown-search
          v-if="(maxInitialToShow > index || allOptionsById[index]?.length > 0)
            && dsComponentDetails.version === VersionOptions.LEGACY"
          v-bind="definition.props"
          v-model:value="definition.value"
          :options="optionsById[index]"
          :data-testid="definition.key"
          :disabled="!(allOptionsById[index]?.length>0)"
          @update:value="emitValue($event,index)"
          @search="handleSearch($event, index)"
        >
          <template #selection="scope">
            <sb-selected-dropdown-search-option
              :value="mapSelections(scope?.selection)"
            />
          </template>
          <template #default="scope">
            <sb-dropdown-option>
              {{ scope.option.displayName }}
            </sb-dropdown-option>
          </template>
        </sb-dropdown-search>
        <sb-auto-complete
          v-if="(maxInitialToShow > index || allOptionsById[index]?.length > 0)
            && dsComponentDetails.version === 'next'"
          v-bind="definition.props"
          v-model="definition.value"
          option-label="displayName"
          :suggestions="optionsById[index]"
          :data-testid="definition.key"
          :disabled="!(allOptionsById[index]?.length>0)"
          :min-length="3"
          fluid
          @update:model-value="emitValue($event,index)"
          @complete="handleSearch($event, index)"
        />
      </template>
      <slot v-else />
    </div>
  </div>
</template>

<script>
import SbDropdownSearch from '@src/components/Dropdown/SbDropdownSearch.vue';
import SbDropdown from '@src/components/Dropdown/SbDropdown.vue';
import SbOption from '@src/components/Dropdown/components/SbOption.vue';
import SbSelectedOption from '@src/components/Dropdown/components/SbSelectedOption.vue';
import SbDropdownOption from '@src/components/Dropdown/SbDropdownOption.vue';
import SbSelectedDropdownSearchOption from '@src/components/Dropdown/SbSelectedDropdownSearchOption.vue';
import DropdownOperators from '@src/components/Filter/components/collapsable/DropdownOperators.vue';
import SbSelect from '@src/components/Select/SbSelect.vue';
import SbAutoComplete from '@src/components/AutoComplete/SbAutoComplete.vue';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';

export default {
  name: 'DropdownChain',
  components: {
    SbDropdownSearch,
    SbSelectedOption,
    SbDropdown,
    SbSelectedDropdownSearchOption,
    SbDropdownOption,
    SbOption,
    DropdownOperators,
    SbSelect,
    SbAutoComplete,
  },
  mixins: [versionedComponentsMixin],
  props: {
    filter: {
      type: Object,
      required: true,
    },
    filterValue: {
      type: Object,
      default: () => { },
    },
  },
  emits: [
    'update:filter-value',
  ],
  data() {
    return {
      optionsById: [],
      optionsDataRefs: [],
      allOptionsById: [],
      dropdDefs: [],
      maxInitialToShow: 0,
    };
  },
  computed: {
    hasOperators() {
      return this.filter && this.filter.operators?.length;
    },
    operatorsList() {
      return this.filter?.operators || [];
    },
    internalOperator: {
      get() {
        return this.filterValue?.operator ?? this.filter.defaultOperator;
      },
      set(value) {
        this.$emit('update:filter-value', {
          operator: value,
          value: this.filterValue?.value,
        });
      },
    },
  },
  watch: {
    'filterValue.value': function (newValue) {
      this.setValue(newValue);
    },
  },
  mounted() {
    this.dropdDefs = this.filter.uiConfigs.map((def) => ({
      ...def,
      value: def.props?.value,
    }));
    this.optionsById = new Array(this.dropdDefs.length);
    this.allOptionsById = new Array(this.dropdDefs.length);
    this.optionsDataRefs = new Array(this.dropdDefs.length);
    this.maxInitialToShow = this.filter.props?.maxInitialToShow || Infinity;

    this.updateOptionsDataProps(0);
    this.setValue(this.filterValue?.value);
  },
  methods: {
    setValue(newValueP) {
      const newValue = newValueP || [];
      this.updateChildrenRefs(0);
      for (let index = 0; index < this.dropdDefs.length; index += 1) {
        const definition = this.dropdDefs[index];
        const option = newValue[index];
        if (!option?.value) {
          this.dropdDefs[index].value = this.getResetValue(this.dropdDefs, index);
        }
        if (!this.containsOption(option?.value, this.optionsById[index])) {
          this.populateNextOptions(option?.value, definition, index, false);
          if (!this.containsOption(option?.value, this.optionsById[index])) {
            this.dropdDefs[index].value = this.getResetValue(this.dropdDefs, index);
            for (let i = index + 1; i < this.dropdDefs.length; i += 1) this.resetPosition(i);
            break;
          }
        } else {
          this.updateOptionsDataProps(index + 1, option.value);
          this.dropdDefs[index].value = option.value;
        }
      }
    },
    containsOption(key, list) {
      if (Array.isArray(key)) {
        return list?.some((option) => key.some((option2) => option2.id === option.id));
      }
      if (!key) return false;
      return list?.some((option) => option.id === (key?.id || key));
    },
    isArray(value) {
      return Array.isArray(value);
    },
    mapToBaseOption(array) {
      return array.map((option) => ({ id: option.id, displayName: option.name }));
    },
    getResetValue(array, index) {
      const object = array[index];
      if (Array.isArray(object.value)) {
        return [];
      }
      return undefined;
    },
    resetPosition(index) {
      this.optionsById[index] = null;
      this.allOptionsById[index] = null;
      this.optionsDataRefs[index] = null;
      const { value } = this.dropdDefs[index];
      this.dropdDefs[index].value = Array.isArray(value) ? [] : undefined;
    },
    updateChildrenRefs(index, key) {
      if (index === 0) {
        this.optionsDataRefs[index] = this.filter.options;
        return this.filter.options;
      }
      const array = this.optionsDataRefs[index - 1];
      const children = array.find((opt) => opt.id === key)?.children || [];
      this.optionsDataRefs[index] = children;
      return children;
    },
    updateOptionsDataProps(index, option) {
      if (!this.dropdDefs[index]) return;
      const children = this.updateChildrenRefs(index, option?.id || option);
      this.optionsDataRefs[index] = children;
      this.optionsById[index] = this.mapToBaseOption(children);
      this.allOptionsById[index] = [...this.optionsById[index]];
    },
    emitValue(option, index) {
      const { component } = this.dropdDefs[index];
      this.dropdDefs[index].value = component === 'sb-dropdown' && this.dsComponentDetails.version === this.VersionOptions.LEGACY ? (option?.id || option) : option;
      const toEmit = this.dropdDefs.map((definition) => {
        const { value } = definition;
        return { key: definition.key, value, name: definition.props?.label || definition.key };
      });
      this.$emit('update:filter-value', { value: toEmit, operator: this.internalOperator });
    },
    populateNextOptions(option, definition, index) {
      const { component } = this.dropdDefs[index];
      this.updateOptionsDataProps(index + 1, option);
      const def = definition;
      def.value = component === 'sb-dropdown' ? (option?.id || option) : option;
    },
    getSelected(options, id) {
      return options?.find((opt) => opt.id === id)?.displayName;
    },
    mapSelections(selections = []) {
      return selections.map((s) => s.displayName).join(', ');
    },
    handleSearch(event, index) {
      const searchTerm = this.dsComponentDetails.version === this.VersionOptions.LEGACY
        ? event.searchTerm : event.query;
      if (searchTerm.length > 0) {
        this.optionsById[index] = this.allOptionsById[index]?.filter(
          (o) => o.displayName.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      } else {
        this.optionsById[index] = this.allOptionsById[index];
      }
    },
  },
};
</script>

<style lang="scss" module>
@use '@src/components/Filter/style/sb-dropdown-filter.module.scss';
</style>
